import { Alert, Button, Flex, Loader, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconArrowLeft } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import type { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import type { SmartCreateData } from 'src/secret-menu/smart-create/types';
import { useSmartCreateLookup } from 'src/secret-menu/smart-create/hooks/useSmartCreateLookup';

export default function FrnStep({
  data,
  nextStep,
  canGoBack,
  previousStep,
}: StepProps<SmartCreateData>) {
  const { lookup, isLoading, error } = useSmartCreateLookup();

  const form = useForm({
    mode: 'uncontrolled',
    initialValues: { frn: data.frn },
    validate: {
      frn: v => (v.trim().length === 0 ? 'FCA firm reference number is required' : null),
    },
  });

  const handleSubmit = form.onSubmit(async values => {
    const result = await lookup({ frn: values.frn });
    const directors = result.data.activeDirectors;
    const isDirector = directors.some(d => d.toLowerCase().includes(data.lastName.toLowerCase()));
    nextStep({
      ...data,
      frn: values.frn,
      companyName: result.data.companyName,
      companyNumber: result.data.companyNumber,
      registeredAddress: result.data.registeredAddress,
      activeDirectors: directors,
      signatoryPosition: isDirector ? 'Director' : '',
    });
  });

  return (
    <Flex direction="column" flex={1} gap="md" mih={0}>
      <Stack gap={4}>
        <Text size="displaySm" fw={FONT_WEIGHTS.medium} c="gray.10">
          FCA registration number
        </Text>
        <Text size="textMd" c="gray.6">
          We could not find an FCA number for this website. Please enter it manually.
        </Text>
      </Stack>

      <Flex direction="column" flex={1} mih={0} style={{ overflowY: 'auto' }}>
        <form id="smart-create-frn-form" onSubmit={handleSubmit}>
          <Stack gap="sm">
            {error && (
              <Alert color="red" title="Lookup failed">
                {error.message}
              </Alert>
            )}
            <TextInput
              required
              label="FCA firm reference number"
              placeholder="e.g. 123456"
              key={form.key('frn')}
              {...form.getInputProps('frn')}
            />
          </Stack>
        </form>
      </Flex>

      <Stack gap="sm">
        <Flex justify="space-between" align="center" gap="md">
          {canGoBack && (
            <Button variant="outline-black" size="xl" onClick={() => previousStep()} p="sm">
              <IconArrowLeft size={24} />
            </Button>
          )}
          <Button
            fullWidth
            size="xl"
            type="submit"
            form="smart-create-frn-form"
            disabled={isLoading}
            leftSection={isLoading ? <Loader size="sm" color="gray.4" /> : undefined}
          >
            Continue
          </Button>
        </Flex>
      </Stack>
    </Flex>
  );
}
